import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Tab,
  TabList,
  Text,
  useColorModeValue,
  useMultiStyleConfig,
  useTab
} from "@chakra-ui/react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { CATEGORIES_STEPS } from "../../const";
import { useDispatch } from "react-redux";
import { setAction } from "../../../../../../context/Store/slices/questionnaireSlice";
import { forwardRef } from "react";

const CustomTab = forwardRef((props, ref) => {
  const tabProps = useTab({ ...props, ref });
  const styles = useMultiStyleConfig("Tabs", tabProps);

  return (
    <Button
      __css={styles.tab}
      {...tabProps}
      color={"inherit !important"}
      borderBottom={0}
      borderColor={`${tabProps.borderColor} !important`}
      type="submit"
    >
      {tabProps.children}
    </Button>
  );
});

function TabStepsList({ currentIndex, categories, tabsCollapsed, setTabsCollapsed }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const dispatch = useDispatch();

  const listRef = useRef();
  const selectedTabRef = useRef();

  const timelineBg = useColorModeValue("gray.200", "gray.700");

  const indicatorBg = useColorModeValue("white", "gray.800");
  const indicatorSelectedBg = "esg_active";
  const indicatorColor = useColorModeValue("black", "white");
  const indicatorRing = useColorModeValue(
    `0 0 0 0.5rem var(--chakra-colors-white)`,
    `0 0 0 0.5rem var(--chakra-colors-gray-800)`
  );

  useEffect(() => {
    const selectedTab = selectedTabRef.current;
    const tabList = listRef.current;

    if (tabList && selectedTab) {
      const tabDistanceFromTop = selectedTab.offsetTop;
      tabList.scrollTop = tabDistanceFromTop;
    }
  }, [currentIndex]);

  return (
    <TabList
      {...styles.tabList({ tabsCollapsed, timelineBg })}
      ref={listRef}
    >
      {categories.map((tabKey, index) => {
        const categoryNumber = index + 1;
        const isSelected = currentIndex === index;
        return (
          <CustomTab
            key={`tab_${tabKey}`}
            {...styles.tab({ focusColor: indicatorSelectedBg })}
            ref={(el) => {
              if (isSelected) {
                selectedTabRef.current = el;
              }
            }}
            onClick={() => dispatch(setAction(index))}
            form={`formStep${categories[currentIndex]}`}
            borderColor={"#B2F1DE"}
            borderInlineEndWidth="0"
          >
            <Box {...styles.tabContent}>
              <Box
                {...styles.tabIndicator({
                  isSelected,
                  indicatorBg,
                  indicatorSelectedBg,
                  indicatorColor,
                  indicatorRing
                })}
              >
                {CATEGORIES_STEPS.indexOf(tabKey) + 1}
              </Box>
              <Text as="strong" {...styles.tabTitle({ isSelected })}>
                {t(`categories.${tabKey}`, { number: categoryNumber }).split(":")[0]}
              </Text>
              <Text as="span" {...styles.tabSubTitle({ isSelected })}>
                {t(`categories.${tabKey}`, { number: categoryNumber }).split(":")[1]}
              </Text>
            </Box>
          </CustomTab>
        );
      })}
      <Tab {...styles.collapseTab({ bg: indicatorBg })}>
        <Flex inlineSize="full">
          <IconButton
            onClick={() => setTabsCollapsed((prev) => !prev)}
            variant="outline"
            size="xs"
            icon={tabsCollapsed ? <FaArrowRight /> : <FaArrowLeft />}
          />
        </Flex>
      </Tab>
    </TabList>
  );
}

export const styles = {
  tabList: ({ tabsCollapsed, timelineBg }) => ({
    display: "flex",
    flexDir: "column",
    flexShrink: 0,
    gap: 2,
    alignItems: "flex-start",
    inlineSize: tabsCollapsed ? 10 : 60,
    overflowX: "hidden",
    overflowY: "auto",
    overscrollBehavior: "contain",
    scrollBehavior: "smooth",
    scrollSnapType: "block proximity",
    transition: "inline-size",
    transitionDuration: "150ms",
    _before: {
      content: "''",
      position: "absolute",
      insetInlineStart: 4,
      insetBlock: 0,
      inlineSize: "1px",
      bg: timelineBg
    }
  }),
  tab: ({ focusColor }) => ({
    inlineSize: "full",
    paddingInline: 0,
    position: "relative",
    scrollSnapAlign: "start",
    _focus: {
      outline: "none",
      borderColor: focusColor,
      borderInlineEndWidth: "thick"
    },
    borderInlineEndWidth: "thick",
    borderColor: "transparent"
  }),
  collapseTab: ({ bg }) => ({
    as: "div",
    position: "sticky",
    insetBlockEnd: "-0.5",
    inlineSize: "full",
    paddingInline: "1",
    borderBlockStartWidth: "thin",
    isDisabled: true,
    bg
  }),
  tabContent: {
    alignItems: "flex-start",
    display: "flex",
    flexDir: "column",
    flexShrink: 0,
    inlineSize: "full",
    paddingInlineStart: 12,
    textAlign: "start"
  },
  tabIndicator: ({
                   isSelected,
                   indicatorBg,
                   indicatorSelectedBg,
                   indicatorColor,
                   indicatorRing
                 }) => ({
    alignItems: "center",
    bg: isSelected ? indicatorSelectedBg : indicatorBg,
    blockSize: 8,
    borderRadius: "full",
    borderWidth: "thin",
    color: isSelected ? "black" : indicatorColor,
    display: "flex",
    fontWeight: "bold",
    inlineSize: 8,
    insetBlockStart: 4,
    insetInlineStart: 0,
    justifyContent: "center",
    position: "absolute",
    sx: {
      boxShadow: indicatorRing
    }
  }),
  tabTitle: ({ isSelected }) => ({
    fontSize: "md",
    inlineSize: 40,
    opacity: isSelected ? 1 : 0.5
  }),
  tabSubTitle: ({ isSelected }) => ({
    display: "block",
    fontSize: "sm",
    inlineSize: 40,
    opacity: isSelected ? 1 : 0.5
  })
};

export default TabStepsList;
